/* One category table (Employees / IWO Employees / Subcontractors) of the Details tab: its own
   title bar, its own 2-row header (month banner + Contract ID / Contract Name / Employee Name / LCAT) and its own pagination.
   Row n holds the n-th planned hire of each month. Page state is local, so paging one table
   never moves the others. */
import React, { useEffect, useState } from "react";
import { TABLE_FONT, TH_BG, TH_TEXT, TH_BORDER, TH_ACTUAL_BG, TH_ACTUAL_TEXT, TH_BG_BAND } from "../../reportTableConfig";
import { monthKeyLabel } from "./fteReportUtils";
import FtePaginationBar from "./FtePaginationBar";

const FONT = TABLE_FONT;
const CONTRACT_ID_W = 110;
const CONTRACT_NAME_W = 200;
const NAME_W = 200;
const LCAT_W = 170;
// Four columns per month, in display order: Contract ID, Contract Name, Employee Name, LCAT.
const COLUMNS = [
  { label: "Contract ID", width: CONTRACT_ID_W, value: (slot) => slot?.contractId },
  { label: "Contract Name", width: CONTRACT_NAME_W, value: (slot) => slot?.contractName },
  { label: "Employee Name", width: NAME_W, value: (slot) => slot?.name },
  { label: "LCAT", width: LCAT_W, value: (slot) => slot?.lcat },
];
const MONTH_W = COLUMNS.reduce((n, c) => n + c.width, 0);

// Same header look as Summary.js's thStyle (shared reportTableConfig tokens). Two sticky rows,
// so the sub-header sits under the banner.
const thBase = {
  position: "sticky",
  background: TH_BG,
  color: TH_TEXT,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  padding: "10px 10px",
  border: "none",
  borderLeft: `1px solid ${TH_BORDER}`,
  boxShadow: `inset 0 -1px 0 ${TH_BORDER}`,
};
// Actual months get the same header colors as Summary.js / Monthly T&M Hours.
const actualColors = (isActual) => (isActual ? { background: TH_ACTUAL_BG, color: TH_ACTUAL_TEXT } : {});
const thTop = (isActual) => ({ ...thBase, ...actualColors(isActual), top: 0, textAlign: "center" });
const thSub = (w, isActual) => ({ ...thBase, ...actualColors(isActual), top: 33, textAlign: "left", minWidth: w, maxWidth: w });
const tdCell = (w, alt) => ({
  padding: "8px 10px",
  fontSize: 12.5,
  color: "#111827",
  background: alt ? "#F8FAFC" : "#fff",
  borderBottom: "1px solid #E2E8F0",
  borderLeft: "1px solid #EEF0F4",
  minWidth: w,
  maxWidth: w,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});
const titleBar = {
  background: TH_BG_BAND,
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  padding: "8px 12px",
};

// Hover text: the name (placeholder + notes) plus the slot's CLIN/contract and hours when present.
const slotTooltip = (slot) =>
  [
    slot.name,
    slot.projectId && `Project: ${slot.projectId}`,
    slot.type && `${slot.type}: ${slot.hours ?? 0} hrs`,
  ].filter(Boolean).join(String.fromCharCode(10));

const cellTitle = (label, slot, text) => {
  if (!slot) return undefined;
  if (label === "Employee Name") return slotTooltip(slot);
  if (label === "LCAT") return [slot.lcat, slot.lcatDescription].filter(Boolean).join(" — ") || undefined;
  return text || undefined;
};

export default function DetailsSectionTable({ category, rows, visibleMonths, actualMonths = [] }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // New data → back to the first page.
  useEffect(() => setPage(1), [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const tableW = visibleMonths.length * MONTH_W;

  return (
    <div style={{ borderTop: "1px solid #E2E8F0" }}>
      <div style={titleBar}>
        {category} <span style={{ fontWeight: 400, opacity: 0.8 }}>({rows.length})</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: tableW, fontFamily: FONT }}>
          <thead>
            <tr>
              {visibleMonths.map((m) => (
                <th key={m} style={thTop(actualMonths.includes(m))} colSpan={COLUMNS.length}>{monthKeyLabel(m)}</th>
              ))}
            </tr>
            <tr>
              {visibleMonths.map((m) => (
                <React.Fragment key={m}>
                  {COLUMNS.map((c) => (
                    <th key={c.label} style={thSub(c.width, actualMonths.includes(m))}>{c.label}</th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={Math.max(visibleMonths.length * COLUMNS.length, 1)} style={{ padding: 24, textAlign: "center", color: "#94A3B8", fontSize: 12.5 }}>
                  No planned hires.
                </td>
              </tr>
            )}
            {pageRows.map((row, i) => (
              <tr key={`${category}-${row.index ?? i}`}>
                {visibleMonths.map((m) => {
                  const slot = row.months?.[m];
                  return (
                    <React.Fragment key={m}>
                      {COLUMNS.map((c) => {
                        const text = c.value(slot);
                        return (
                          <td
                            key={c.label}
                            style={tdCell(c.width, i % 2 === 1)}
                            title={cellTitle(c.label, slot, text)}
                          >
                            {text || ""}
                          </td>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <FtePaginationBar
        page={safePage}
        pageSize={pageSize}
        totalRows={rows.length}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />
    </div>
  );
}
